// One report or group in the left-rail feed. A severity-ranked left border (red / amber / fog / none)
// and a small glyph replace the old "significant = amber, everything else quiet" rule (spec §5 "feed
// triage", 2026-09-21 pitches): a broken platoon reads differently from an acknowledged order even
// though both used to be the same amber-or-not choice. Hovering a report that knows where it happened
// pings that spot on the map; a group (same-kind traffic rolled up within one sim minute,
// `src/ui/feed/triage.ts`) never pings, since it stands for several places at once.
//
// A SPOTREP (spot report) or SITREP (situation report) may also carry a language-model enrichment (spec
// §6 "grounded narration" - `src/state/feed.ts`): an S-2 (intelligence officer) assessment or a rephrased
// SITREP, shown under the report text with a confidence chip, citation chips that ping the map on hover,
// and a "template"/"flagged"/"restated" chip when the model was unavailable, its citations were mostly
// invalid, or its wording just repeated the already-known facts.

import { memo } from 'react';
import { confidenceBand, estimateLabel } from '../../ai/estimator';
import { calibrationStatus } from '../../ai/calibration';
import { ROLE_LABEL } from '../../ai/contactEstimate';
import type { Enrichment } from '../../state/feed';
import type { Report } from '../reports';
import { clockHM } from '../reports';
import { isGroup, severityColor, severityGlyph, severityOf, type FeedItem } from '../feed/triage';
import { Acronym, AcronymText } from '../Acronym';
import { Chip, type ChipTone } from './ui';

const CONFIDENCE_TONE: Record<Enrichment['confidence'], ChipTone> = {
  low: 'warn',
  medium: 'neutral',
  high: 'friendly',
};

/** True for the report kinds the feed ever requests an enrichment for (`src/ui/feed/ReportFeed.tsx`),
 * so a plain "S-2 assessing…" placeholder never shows under a report that will never get one. Groups are
 * never built from these kinds (`src/ui/feed/triage.ts` never groups a contact or a SITREP), but the
 * guard is kept kind-based rather than group-based so it stays true if that ever changes. */
function enrichable(kind: Report['kind']): boolean {
  return kind === 'contact.new' || kind === 'nai.contact' || kind === 'sitrep';
}

/**
 * Memoized on its props, which is what lets the feed mark up its acronyms at all.
 *
 * A report is templated once when its event arrives and then never changes (src/ui/feed/ReportFeed.tsx
 * caches them), so with a memo the `<AcronymText>` split below runs once per card rather than a hundred
 * and sixty times per snapshot.
 */
export const EventCard = memo(EventCardImpl);

function EventCardImpl({
  item, onHover, enrichment, pending, onCiteHover, highlight,
}: {
  item: FeedItem;
  onHover?: (cell: number | null) => void;
  /** The enrichment for this report's event id, once it has arrived. Never set for a group. */
  enrichment?: Enrichment;
  /** An enrichment was requested for this report and has not resolved yet. */
  pending?: boolean;
  /** Hovering a citation chip: pings the cited event's own grid cell, or clears the ping. */
  onCiteHover?: (eventId: number | null) => void;
  /** The newest significant card gets a one-second opacity highlight on arrival - nothing moves or
   * changes colour, spec's "nothing bounces" rule (src/styles.css `.slide-in`) applies here too. */
  highlight?: boolean;
}) {
  const role = enrichment?.role;
  const roleBand = role ? confidenceBand(role.estimate.confidence) : undefined;
  const group = isGroup(item);
  const sev = severityOf(item);
  const borderColor = severityColor(sev);
  const glyph = severityGlyph(sev);
  const cell = group ? undefined : item.cell;
  const hoverable = cell !== undefined;
  const unitTitle = group ? `${item.count} units: ${item.unitIds.length > 0 ? item.unitIds.join(', ') : 'unlisted'}` : undefined;

  return (
    <article
      data-testid="event-card"
      data-kind={item.kind}
      data-significant={item.significant ? 'true' : 'false'}
      data-group={group ? 'true' : 'false'}
      onMouseEnter={hoverable ? () => onHover?.(cell ?? null) : undefined}
      onMouseLeave={hoverable ? () => onHover?.(null) : undefined}
      className={`wt-card slide-in${highlight ? ' feed-highlight' : ''}`}
      style={{
        borderLeft: `2px solid ${borderColor}`,
        cursor: hoverable ? 'crosshair' : 'default',
      }}
    >
      <div className="mono flex items-baseline gap-2 text-[10px] tracking-[0.08em]">
        <span className="text-fog">{clockHM(item.t)}</span>
        {glyph && (
          <span aria-hidden style={{ color: borderColor }} title={sev}>
            {glyph}
          </span>
        )}
        <span style={{ color: item.significant ? 'var(--color-warn)' : 'var(--color-friendly)' }}>
          {/* "S-2" is three characters of staff shorthand doing the work of "the intelligence
              officer"; hovering it says so. A callsign is left alone. */}
          <AcronymText>{item.source}</AcronymText>
        </span>
        {group && (
          <span title={unitTitle} className="text-fog">
            ×{item.count}
          </span>
        )}
      </div>
      <p className="mono mt-0.5 text-[11px] leading-snug text-paper">
        <AcronymText>{item.text}</AcronymText>
      </p>

      {!group && (enrichment || (pending && enrichable(item.kind))) && (
        <div data-testid="enrichment" className="mt-1 border-t border-slate-700/60 pt-1">
          {enrichment ? (
            <>
              <div className="mono flex flex-wrap items-center gap-1">
                <span className="text-[9px] uppercase tracking-[0.15em] text-isr">
                  {item.kind === 'sitrep'
                    ? <Acronym k="sitrep">SITREP</Acronym>
                    : <><Acronym k="s-2">S-2</Acronym> ASSESSMENT</>}
                </span>
                {!role && <Chip tone={CONFIDENCE_TONE[enrichment.confidence]} active title="Narrator's self-reported confidence; not calibrated">narrative {enrichment.confidence}</Chip>}
                {enrichment.source === 'template' && <Chip title="The model was unavailable; this is the deterministic fallback.">template</Chip>}
                {enrichment.source === 'restated' && (
                  <Chip title="The model's wording just repeated the already-known facts almost verbatim, so there is no new line to show.">restated</Chip>
                )}
                {enrichment.flagged && (
                  <Chip tone="warn" title="More than half the model's citations were not events it was actually shown.">flagged</Chip>
                )}
              </div>
              {role && roleBand && (
                <div data-testid="s2-role-estimate" data-provider={role.estimate.provider}
                  className="mono mt-1 flex flex-wrap items-center gap-1 text-[10px] text-isr"
                  title={`Role judgment at ${clockHM(role.tick)}; not position accuracy.${role.estimate.fallbackReason ? ` Fallback: ${role.estimate.fallbackReason}` : ''}`}>
                  <span>Assessed {ROLE_LABEL[role.estimate.value]}</span>
                  <Chip tone={CONFIDENCE_TONE[roleBand]} testId="s2-role-confidence">{roleBand} role confidence</Chip>
                  <span>{estimateLabel(role.estimate, calibrationStatus().runs)}</span>
                  {role.estimate.fallbackReason && <span>· fallback</span>}
                </div>
              )}
              {enrichment.source !== 'restated' && (
                <>
                  <p className="mono mt-0.5 text-[10px] leading-snug text-fog">
                    <AcronymText>{enrichment.text}</AcronymText>
                  </p>
                  {enrichment.cited.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {enrichment.cited.map((id) => (
                        <span
                          key={id}
                          data-testid="cite-chip"
                          className="mono cursor-crosshair rounded-sm border border-slate-700 px-1 text-[9px] text-fog"
                          onMouseEnter={() => onCiteHover?.(id)}
                          onMouseLeave={() => onCiteHover?.(null)}
                        >
                          #{id}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <span className="mono text-[9px] italic text-slate-700">
              <Acronym k="s-2">S-2</Acronym> assessing…
            </span>
          )}
        </div>
      )}
    </article>
  );
}
